import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shield, WarningCircle, Prohibit, BookOpen } from "@phosphor-icons/react";
import { Heart, Brain, Ribbon, Droplet, Loader2 } from "lucide-react";

const RISK_STYLES = {
  safe:    { icon: Shield,        color: "#4ADE80", label: "Safe" },
  caution: { icon: WarningCircle, color: "#FBBF24", label: "Caution" },
  avoid:   { icon: Prohibit,      color: "#F87171", label: "Avoid" },
};
const SPECIALTY_META = {
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,   accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon,  accent: "#FBBF24", label: "Oncology" },
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
};

/**
 * FormularyReference — Phase 2.
 * Curated per-specialty medication reference. Read-only. Powers the
 * "Formulary" tab on the Medications page.
 */
export default function FormularyReference({ defaultSpecialty = "nephrology" }) {
  const [specialty, setSpecialty] = useState(defaultSpecialty);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get(`/platform/formulary/${specialty}`);
        setRows(data.medications || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load formulary");
        setRows([]);
      } finally { setLoading(false); }
    })();
  }, [specialty]);

  const TABS = ["nephrology", "cardiology", "neurology", "oncology"];

  return (
    <div className="ros-card" data-testid="formulary-reference">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={16} className="text-[#60A5FA]" />
        <div className="ros-label">Formulary reference · curated by specialty</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="formulary-tabs">
        {TABS.map((s) => {
          const meta = SPECIALTY_META[s];
          const Icon = meta.icon;
          const active = specialty === s;
          return (
            <button key={s} onClick={() => setSpecialty(s)}
                    data-testid={`formulary-tab-${s}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active ? "text-black border-transparent"
                             : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white"
                    }`}
                    style={active ? { background: meta.accent } : {}}>
              <Icon size={12} /> {meta.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center text-[#A3ACA7] py-8 flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-sm text-[#F87171]">{err}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#A3ACA7] uppercase tracking-wider border-b border-[#272E2B]">
                <th className="py-2 pr-4">Drug</th>
                <th className="py-2 pr-4">Class</th>
                <th className="py-2 pr-4">Indications</th>
                <th className="py-2 pr-4">Caution</th>
                <th className="py-2">Risk</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const rs = RISK_STYLES[m.risk_level] || RISK_STYLES.caution;
                const Icon = rs.icon;
                return (
                  <tr key={m.name} className="border-t border-[#272E2B]"
                      data-testid={`formulary-row-${m.name.split(" ")[0].toLowerCase()}`}>
                    <td className="py-2.5 pr-4 text-white font-medium">
                      {m.name}
                      {m.rxnorm_cui && (
                        <div className="text-[9px] text-[#7A8480] font-mono">RxNorm {m.rxnorm_cui}</div>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-[#A3ACA7]">{m.class}</td>
                    <td className="py-2.5 pr-4 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {(m.indications || []).map((i) => (
                          <span key={i} className="bg-[#1C201E] text-[#D1D5DB] px-1.5 py-0.5 rounded text-[10px]">{i}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-[#A3ACA7] leading-relaxed max-w-xs">{m.cautions}</td>
                    <td className="py-2.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ color: rs.color, background: `${rs.color}18`, border: `1px solid ${rs.color}40` }}>
                        <Icon size={10} /> {rs.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
